import { SentimentPolarity } from '@up2stay/up2promo-api-client';
import { Button } from '@up2stay/up2promo-ui';
import { MapPin, Smile, Star, Tag, X } from 'lucide-react';

import {
  FilterChipOption,
  FilterChipPopover,
} from '@/core/components/layout/filter-chip-popover';
import {
  countActiveChipFilters,
  type ReviewsAspectOption,
  type ReviewsFilterValues,
  type ReviewsLocationOption,
  toggleInArray,
} from '@/core/components/reputation/reviews/reviews-filters';
import type { Dictionary } from '@/core/providers/dictionary-provider';

type ChipsDict = Dictionary['reviews']['filterChips'];

const RATINGS = [5, 4, 3, 2, 1] as const;
const SENTIMENTS = [
  SentimentPolarity.POSITIVE,
  SentimentPolarity.NEUTRAL,
  SentimentPolarity.NEGATIVE,
] as const;

export function ReviewsFilterChips({
  value,
  onChange,
  locationOptions,
  aspectOptions,
  labels,
  sentimentLabels,
}: {
  value: ReviewsFilterValues;
  onChange: (next: ReviewsFilterValues) => void;
  locationOptions: ReviewsLocationOption[];
  aspectOptions: ReviewsAspectOption[];
  labels: ChipsDict;
  sentimentLabels: Record<SentimentPolarity, string>;
}) {
  const activeCount = countActiveChipFilters(value);
  return (
    <div className="flex items-center gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <FilterChipPopover
        icon={Star}
        label={labels.stars}
        count={value.ratings.length}
      >
        {RATINGS.map((rating) => (
          <FilterChipOption
            key={rating}
            selected={value.ratings.includes(rating)}
            onToggle={() =>
              onChange({
                ...value,
                ratings: toggleInArray(value.ratings, rating),
              })
            }
          >
            {/* Stars are decorative; the sr-only text names the option (DEV-540). */}
            <span
              className="tracking-wide text-[color:var(--chart-4)]"
              aria-hidden
            >
              {'★'.repeat(rating)}
            </span>
            <span className="sr-only">
              {(rating === 1 ? labels.starsOne : labels.starsCount).replace(
                '{count}',
                String(rating),
              )}
            </span>
          </FilterChipOption>
        ))}
      </FilterChipPopover>

      <FilterChipPopover
        icon={Smile}
        label={labels.sentiment}
        count={value.sentiments.length}
      >
        {SENTIMENTS.map((sentiment) => (
          <FilterChipOption
            key={sentiment}
            selected={value.sentiments.includes(sentiment)}
            onToggle={() =>
              onChange({
                ...value,
                sentiments: toggleInArray(value.sentiments, sentiment),
              })
            }
          >
            {sentimentLabels[sentiment]}
          </FilterChipOption>
        ))}
      </FilterChipPopover>

      <FilterChipPopover
        icon={Tag}
        label={labels.topics}
        count={value.aspects.length}
      >
        {aspectOptions.length === 0 ? (
          <p className="text-muted-foreground px-2.5 py-2 text-[13px]">
            {labels.noOptions}
          </p>
        ) : (
          aspectOptions.map((aspect) => (
            <FilterChipOption
              key={aspect.key}
              selected={value.aspects.includes(aspect.key)}
              onToggle={() =>
                onChange({
                  ...value,
                  aspects: toggleInArray(value.aspects, aspect.key),
                })
              }
            >
              {aspect.label}
            </FilterChipOption>
          ))
        )}
      </FilterChipPopover>

      {locationOptions.length > 1 ? (
        <FilterChipPopover
          icon={MapPin}
          label={labels.location}
          count={value.locationIds.length}
        >
          {locationOptions.map((loc) => (
            <FilterChipOption
              key={loc.id}
              selected={value.locationIds.includes(loc.id)}
              onToggle={() =>
                onChange({
                  ...value,
                  locationIds: toggleInArray(value.locationIds, loc.id),
                })
              }
            >
              {loc.name}
            </FilterChipOption>
          ))}
        </FilterChipPopover>
      ) : null}

      {activeCount > 0 ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-muted-foreground shrink-0"
          onClick={() =>
            onChange({
              ...value,
              locationIds: [],
              sentiments: [],
              aspects: [],
              ratings: [],
            })
          }
        >
          <X className="mr-1 size-3.5" aria-hidden />
          {labels.clear}
        </Button>
      ) : null}
    </div>
  );
}
